import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Layout from "../components/Layout";
import qrLogo from "../assets/images/qr-logo.svg";
import apiFetch from "../utils/apiFetch";
import {
  DASH,
  EQUIPMENT_PASSPORT_TYPES,
  collectRecordFiles,
  displayValue,
  formatDate,
  getEquipmentTitle,
  getFieldRows,
  getFileId,
  sortByDateDesc,
} from "../utils/equipmentPassportConfig";

const DIRECTUS_URL =
  process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";
const PUBLIC_PASSPORT_ORIGIN =
  process.env.REACT_APP_PUBLIC_PASSPORT_ORIGIN ||
  "https://map.uzbekistonmet.uz";
const PUBLIC_EQUIPMENT_TYPES = new Set([
  "transformers",
  "breakers",
  "disconnectors",
  "current-transformers",
  "voltage-transformers",
  "reactors",
  "batteries",
  "arresters",
]);

function toCompactPublicId(value) {
  const hex = String(value || "").replace(/-/g, "");
  if (!/^[0-9a-f]{32}$/i.test(hex)) return null;

  const bytes = hex.match(/.{2}/g).map((pair) => parseInt(pair, 16));
  const binary = String.fromCharCode(...bytes);
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function PassportState({ title, text, tone = "muted" }) {
  return (
    <Layout>
      <div className="container">
        <div className={`passport-state passport-state--${tone}`}>
          <h1>{title}</h1>
          {text && <p>{text}</p>}
        </div>
      </div>
    </Layout>
  );
}

function FieldGrid({ fields }) {
  return (
    <dl className="passport-field-grid">
      {fields.map((field) => (
        <div className="passport-field" key={field.key || field.label}>
          <dt>{field.label}</dt>
          <dd>{displayValue(field.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function EquipmentQrCard({ slug, equipmentType, title }) {
  const qrSvgRef = useRef(null);

  const publicUrl = useMemo(() => {
    if (!slug) return null;
    const origin = PUBLIC_PASSPORT_ORIGIN.replace(/\/+$/, "");
    const publicId = toCompactPublicId(slug);
    return publicId
      ? `${origin}/p/${publicId}`
      : `${origin}/public/${encodeURIComponent(slug)}`;
  }, [slug]);

  const downloadQr = async () => {
    const sourceSvg = qrSvgRef.current;
    if (!sourceSvg || !publicUrl) return;

    try {
      const svgNamespace = "http://www.w3.org/2000/svg";
      const outputSvg = sourceSvg.cloneNode(true);
      const logoImage = outputSvg.querySelector("image");

      if (logoImage) {
        const logoResponse = await fetch(qrLogo);
        if (!logoResponse.ok) throw new Error("QR logo could not be loaded");

        const logoDocument = new DOMParser().parseFromString(
          await logoResponse.text(),
          "image/svg+xml"
        );
        if (logoDocument.querySelector("parsererror")) {
          throw new Error("QR logo SVG is invalid");
        }

        const logoRoot = logoDocument.documentElement;
        const inlineLogo = document.createElementNS(svgNamespace, "svg");
        ["x", "y", "width", "height", "opacity"].forEach((attribute) => {
          const value = logoImage.getAttribute(attribute);
          if (value !== null) inlineLogo.setAttribute(attribute, value);
        });
        inlineLogo.setAttribute("viewBox", logoRoot.getAttribute("viewBox"));
        inlineLogo.setAttribute("preserveAspectRatio", "xMidYMid meet");

        Array.from(logoRoot.childNodes).forEach((child) => {
          inlineLogo.appendChild(document.importNode(child, true));
        });
        logoImage.replaceWith(inlineLogo);
      }

      outputSvg.setAttribute("width", "100mm");
      outputSvg.setAttribute("height", "100mm");

      const source = new XMLSerializer().serializeToString(outputSvg);
      const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${source}`], {
        type: "image/svg+xml;charset=utf-8",
      });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `qr-${equipmentType}-${slug}.svg`;
      link.href = objectUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error("Failed to download QR SVG", error);
      window.alert("QR-кодни SVG форматда юклаб бўлмади.");
    }
  };

  return (
    <section className="passport-card passport-qr-card">
      <div className="passport-card__head passport-card__head--compact">
        <span className="passport-section-number">QR</span>
        <div>
          <h2>Очиқ паспорт QR-коди</h2>
          {/* <p>Босиб чиқариш учун доимий ҳавола.</p> */}
        </div>
      </div>

      {publicUrl ? (
        <div className="passport-qr-card__body">
          <div className="passport-qr-card__canvas">
            <QRCodeSVG
              ref={qrSvgRef}
              value={publicUrl}
              title={`${title} очиқ паспорти`}
              size={2048}
              level="Q"
              boostLevel={false}
              marginSize={4}
              bgColor="#ffffff"
              fgColor="#071a2d"
              imageSettings={{
                src: qrLogo,
                width: 360,
                height: 360,
                opacity: 1,
                excavate: true,
              }}
            />
          </div>

          {/* <code>{publicUrl}</code> */}

          <div className="passport-qr-card__actions">
            <button type="button" className="passport-action" onClick={downloadQr}>
              QR-кодни SVG форматда юклаш
            </button>
            <a
              className="passport-action passport-action--secondary"
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
            >
              Очиқ паспортни кўриш
            </a>
          </div>
        </div>
      ) : (
        <div className="passport-empty">
          QR-код яратиш учун ускунага slug бириктирилиши керак.
        </div>
      )}
    </section>
  );
}

function Timeline({ items, emptyText, type = "maintenance" }) {
  if (!items.length) {
    return <div className="passport-empty">{emptyText}</div>;
  }

  return (
    <div className="passport-timeline">
      {items.map((item, index) => {
        const author = item.author_user
          ? `${item.author_user.lastname || ""} ${item.author_user.name || ""}`.trim()
          : null;
        const date = item.event_date || item.date || item.event_datetime || item.date_created;

        return (
          <article className="passport-timeline__item" key={item.id || index}>
            <div className="passport-timeline__rail" />
            <div className="passport-timeline__body">
              <div className="passport-timeline__top">
                <span className={`passport-chip passport-chip--${type}`}>
                  {formatDate(date)}
                </span>
                {author && <span className="passport-timeline__author">{author}</span>}
              </div>
              <h3>{item.topic || item.protection_type || item.result || "Ёзув"}</h3>
              {item.section && <p>Участка: {item.section}</p>}
              {item.cause && <p>Сабаб: {item.cause}</p>}
              {item.measures && <p>Чоралар: {item.measures}</p>}
              {item.comment && <p>{item.comment}</p>}
              {item.result && <p>Натижа: {item.result}</p>}
              {Array.isArray(item.files) && item.files.length > 0 && (
                <p className="passport-timeline__files">
                  Файллар: {item.files.length}
                </p>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function FileGrid({ files }) {
  const safeFiles = (Array.isArray(files) ? files : [])
    .map((file) => ({ ...file, fileId: getFileId(file) }))
    .filter((file) => file.fileId);

  if (!safeFiles.length) {
    return <div className="passport-empty">Файллар бириктирилмаган.</div>;
  }

  return (
    <div className="passport-file-grid">
      {safeFiles.map((file, index) => (
        <a
          className="passport-file-card"
          href={`${DIRECTUS_URL}/assets/${file.fileId}`}
          target="_blank"
          rel="noreferrer"
          key={`${file.fileId}-${index}`}
        >
          <span className="passport-file-card__mark">FILE</span>
          <span>{file.record_topic || file.maintenance_topic || `Файл ${index + 1}`}</span>
          <small>{formatDate(file.record_date || file.maintenance_event_date)}</small>
        </a>
      ))}
    </div>
  );
}

const EquipmentPassport = () => {
  const { equipmentType, equipmentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(
          `/api/equipment/${encodeURIComponent(equipmentType)}/${encodeURIComponent(equipmentId)}`,
          { cache: "no-store" }
        );

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "Оборудование не найдено");
        }

        const payload = await res.json();
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(e.message || "Ошибка загрузки паспорта");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (equipmentType && equipmentId) load();
    return () => {
      cancelled = true;
    };
  }, [equipmentType, equipmentId]);

  const localCfg = EQUIPMENT_PASSPORT_TYPES[equipmentType];

  const view = useMemo(() => {
    if (!data) return null;
    const cfg = localCfg || {
      label: data.type_label || equipmentType,
      fields: [],
      titleKeys: ["name"],
    };
    const equipment = data.equipment || {};
    const technicalFields = Array.isArray(data.technical_fields) && data.technical_fields.length
      ? data.technical_fields
      : getFieldRows(equipment, cfg);
    const maintenance = sortByDateDesc(data.maintenance);
    const files = Array.isArray(data.files) && data.files.length
      ? data.files
      : collectRecordFiles(maintenance);

    return {
      cfg,
      equipment,
      title: getEquipmentTitle(equipment, cfg),
      technicalFields,
      maintenance,
      files,
      substation: data.substation || null,
      links: data.links || {},
    };
  }, [data, equipmentType, localCfg]);

  if (loading) {
    return <PassportState title="Паспорт юкланмоқда" text="Ускуна маълумотлари тайёрланмоқда." />;
  }

  if (error || !view) {
    return (
      <PassportState
        title="Ускуна паспорти топилмади"
        text={error || "URL параметрларини текширинг."}
        tone="error"
      />
    );
  }

  const substationUrl = view.links.substation_passport || (
    view.substation?.slug ? `/indicators/substations/${view.substation.slug}` : null
  );

  return (
    <Layout>
      <div className="container">
        <main className="passport-page passport-page--equipment">
          <section className="passport-hero passport-hero--equipment">
            <div className="passport-breadcrumbs">
              <Link to="/">Бош саҳифа</Link>
              <span>/</span>
              {substationUrl ? (
                <Link to={substationUrl}>Подстанция</Link>
              ) : (
                <span>Подстанция</span>
              )}
              <span>/</span>
              <span>{view.cfg.label}</span>
            </div>

            <div className="passport-hero__grid">
              <div>
                <span className="passport-kicker">Ускуна паспорти</span>
                <h1>{view.title}</h1>
                <p>
                  {view.cfg.label} бўйича техник маълумотлар, эксплуатация тарихи ва боғланган файллар.
                </p>
              </div>

              <aside className="passport-identity">
                <span className="passport-identity__label">Паспорт ID</span>
                <strong>#{displayValue(view.equipment.id)}</strong>
                <span>{view.cfg.label}</span>
              </aside>
            </div>
          </section>

          <section className="passport-metrics">
            <div className="passport-metric">
              <span>Подстанция</span>
              <strong>{view.substation?.name || DASH}</strong>
            </div>
            <div className="passport-metric">
              <span>Филиал</span>
              <strong>{view.substation?.city?.name || DASH}</strong>
            </div>
            <div className="passport-metric">
              <span>Кучланиш</span>
              <strong>{displayValue(view.substation?.voltage)}</strong>
            </div>
            <div className="passport-metric">
              <span>Эксплуатация</span>
              <strong>{view.maintenance.length}</strong>
            </div>
          </section>

          <div className="passport-layout">
            <div className="passport-main">
              <section className="passport-card">
                <div className="passport-card__head">
                  <span className="passport-section-number">01</span>
                  <div>
                    <h2>Техник характеристикалар</h2>
                    <p>Асосий паспорт кўрсаткичлари ва идентификация маълумотлари.</p>
                  </div>
                </div>
                <FieldGrid fields={view.technicalFields} />
              </section>

              <section className="passport-card">
                <div className="passport-card__head">
                  <span className="passport-section-number">02</span>
                  <div>
                    <h2>Эксплуатация ва таъмир</h2>
                    <p>Ускуна бўйича эксплуатация ва бириктирилган файллар.</p>
                  </div>
                </div>
                <Timeline
                  items={view.maintenance}
                  emptyText="Эксплуатация бўйича маълумот йўқ."
                />
              </section>

            </div>

            <aside className="passport-side">
              {PUBLIC_EQUIPMENT_TYPES.has(equipmentType) && (
                <EquipmentQrCard
                  slug={view.equipment.slug}
                  equipmentType={equipmentType}
                  title={view.title}
                />
              )}
              <section className="passport-card">
                <div className="passport-card__head passport-card__head--compact">
                  <span className="passport-section-number">F</span>
                  <div>
                    <h2>Файллар</h2>
                    <p>Боғланган медиа.</p>
                  </div>
                </div>
                <FileGrid files={view.files} />
              </section>
            </aside>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default EquipmentPassport;
